import { useState, useEffect, useCallback } from 'react';
import { LuSearch, LuRefreshCw } from 'react-icons/lu';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import TableCard from '../../components/TableCard/TableCard';
import StatusBadge from '../../components/StatusBadge/StatusBadge';
import CustomSelect from '../../components/CustomSelect/CustomSelect';
import DisputeDrawer from '../../components/DisputeDrawer/DisputeDrawer';
import { fetchDisputes, updateDispute, getAssetUrl } from '../../lib/directus';
import styles from './Disputes.module.css';

const STATUS_OPTIONS = ['All status', 'Open', 'Closed'];

function formatRelativeTime(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function mapDispute(d) {
  const order = d.order || {};
  const batch = order.batch || {};
  const status = (d.status || 'open').toLowerCase() === 'closed' ? 'Closed' : 'Open';

  const evidence = Array.isArray(d.evidence)
    ? d.evidence.map(e => {
        const fileId = typeof e === 'string' ? e : (e.directus_files_id || e.id);
        return fileId ? getAssetUrl(fileId) : null;
      }).filter(Boolean)
    : [];

  return {
    id: d.id,
    disputeId: d.dispute_id ? `#${d.dispute_id}` : `#DSP-${String(d.id).slice(-8).toUpperCase()}`,
    orderId: order.order_id
      ? `#${order.order_id}`
      : order.id ? `#ORD-${String(order.id).padStart(6, '0')}` : '—',
    type: d.issue_type || d.type || '—',
    buyer: order.buyer?.company_name || '—',
    seller: batch.seller?.company_name || '—',
    opened: formatRelativeTime(d.date_created),
    status,
    reportedBy: d.reported_by?.company_name || order.buyer?.company_name || '—',
    reportedAt: d.date_created
      ? new Date(d.date_created).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
      : '—',
    amount: d.amount ? `R${parseFloat(d.amount).toLocaleString()}` : '—',
    commodity: batch.commodity
      ? `${batch.commodity}${batch.grade ? ` (${batch.grade})` : ''}`
      : '—',
    transporter: d.transporter?.company_name || '—',
    statement: d.statement || d.description || '—',
    evidence,
  };
}

export default function Disputes() {
  const [disputes, setDisputes]    = useState([]);
  const [loading, setLoading]      = useState(true);
  const [search, setSearch]        = useState('');
  const [statusFilter, setStatus]  = useState('All status');
  const [selected, setSelected]    = useState([]);
  const [activeDispute, setActive] = useState(null);
  const [resolving, setResolving]  = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetchDisputes()
      .then(data => setDisputes(data.map(mapDispute)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = disputes.filter(row => {
    const matchSearch =
      row.disputeId.toLowerCase().includes(search.toLowerCase()) ||
      row.orderId.toLowerCase().includes(search.toLowerCase()) ||
      row.type.toLowerCase().includes(search.toLowerCase()) ||
      row.buyer.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'All status' || row.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const toggleRow = id =>
    setSelected(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const toggleAll = () =>
    setSelected(selected.length === filtered.length ? [] : filtered.map(r => r.id));

  async function handleResolve(data) {
    if (!activeDispute) return;
    setResolving(true);
    try {
      await updateDispute(activeDispute.id, {
        status: 'Closed',
        resolution_decision: data.decision,
        resolution_notes: data.notes,
        ...(data.amountBuyer  && { resolution_amount_buyer:  data.amountBuyer }),
        ...(data.amountSeller && { resolution_amount_seller: data.amountSeller }),
      });
      setActive(null);
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setResolving(false);
    }
  }

  const toolbar = (
    <>
      <div className={styles.toolbarLeft}>
        <div className={styles.searchWrapper}>
          <LuSearch className={styles.searchIcon} />
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search disputes..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <CustomSelect
          value={statusFilter}
          onChange={setStatus}
          options={STATUS_OPTIONS}
        />
      </div>
      <button className={styles.refreshBtn} onClick={() => { setSearch(''); setStatus('All status'); load(); }}>
        <LuRefreshCw size={13} />
        Refresh
      </button>
    </>
  );

  const pagination = (
    <>
      <span className={styles.pageInfo}>Total {filtered.length} items</span>
      <button className={styles.pageNav}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <button className={`${styles.pageBtn} ${styles.pageBtnActive}`}>1</button>
      <button className={styles.pageNav}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6" />
        </svg>
      </button>
    </>
  );

  return (
    <AdminLayout title="Disputes">
      <DisputeDrawer
        dispute={activeDispute}
        onClose={() => setActive(null)}
        onResolve={handleResolve}
        resolving={resolving}
      />

      <TableCard toolbar={toolbar} pagination={pagination}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxCol}>
                <input
                  type="checkbox"
                  checked={selected.length === filtered.length && filtered.length > 0}
                  onChange={toggleAll}
                />
              </th>
              <th>Dispute ID</th>
              <th>Order ID</th>
              <th>Type</th>
              <th>Buyer</th>
              <th>Seller</th>
              <th>Opened</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className={styles.loadingCell}>Loading...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className={styles.loadingCell}>No disputes found.</td></tr>
            )}
            {!loading && filtered.map(row => (
              <tr key={row.id} onClick={() => setActive(row)} style={{ cursor: 'pointer' }}>
                <td onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={selected.includes(row.id)}
                    onChange={() => toggleRow(row.id)}
                  />
                </td>
                <td className={styles.disputeId}>{row.disputeId}</td>
                <td>{row.orderId}</td>
                <td>{row.type}</td>
                <td>{row.buyer}</td>
                <td>{row.seller}</td>
                <td>{row.opened}</td>
                <td><StatusBadge status={row.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableCard>
    </AdminLayout>
  );
}
